import { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { fmtL, fmtNum, apiErr } from "@/lib/format";
import { toast } from "sonner";
import { ChartLineIcon, FlowerIcon, BoxArrowDownIcon, CheckCircleIcon, XIcon } from "@phosphor-icons/react";
import { useModalForm } from "@/lib/modalForm";
import BotonCerrar from "@/components/BotonCerrar";

export default function DoubleCostReport() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState([]);
  const [closeOpen, setCloseOpen] = useState(false);
  const [mes, setMes] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  });
  const [selected, setSelected] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/inventory/double-cost-report");
      setRows(data);
    } catch (e) { toast.error(apiErr(e)); }
    finally { setLoading(false); }
  };
  // eslint-disable-next-line
  useEffect(() => { load(); }, []);

  const loadPreview = async () => {
    try {
      const { data } = await api.get("/inventory/close-period/preview", { params: { mes } });
      setPreview(data);
      // pre-select all with no current p0 fijado
      const sel = {};
      data.forEach((r) => { sel[r.producto_id] = { include: true, p0: r.p0_sugerido }; });
      setSelected(sel);
    } catch (e) { toast.error(apiErr(e)); }
  };

  const onClosePeriod = async () => {
    const productos = Object.entries(selected)
      .filter(([, v]) => v.include && Number(v.p0) > 0)
      .map(([producto_id, v]) => ({ producto_id, p0: Number(v.p0) }));
    if (productos.length === 0) { toast.error("Selecciona al menos un producto con P0 válido"); return; }
    try {
      const { data } = await api.post("/inventory/close-period", { mes, productos });
      toast.success(`P0 fijado en ${data.updated} productos para ${mes}`);
      setCloseOpen(false);
      load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const totalPEPS = rows.reduce((s, r) => s + (r.pc_peps || 0) * (r.stock_actual || 0), 0);
  const totalPond = rows.reduce((s, r) => s + (r.pc_ponderado || 0) * (r.stock_actual || 0), 0);

  const m = useModalForm({ value: { mes, selected }, abierto: closeOpen, onClose: () => setCloseOpen(false), onSave: onClosePeriod, mensaje: "¿Está seguro de que desea fijar el P0 de los productos seleccionados?" });
  return (
    <Layout
      title="Doble lectura del costo"
      subtitle="Comparación PC PEPS (real) vs PC ponderado (visual) por producto"
      actions={
        <button
          data-testid="close-period-btn"
          onClick={() => { setCloseOpen(true); loadPreview(); }}
          className="bg-agro-yellow hover:opacity-90 text-black font-medium rounded-md px-4 py-2 text-sm flex items-center gap-2 active:scale-95"
        >
          <CheckCircleIcon size={16} weight="bold" /> Cerrar período & fijar P0
        </button>
      }
    >
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-card border border-border/60 rounded-lg p-4" data-testid="kpi-total-peps">
          <div className="text-[10px] uppercase tracking-wider text-text-muted mb-1.5">Valor inventario (PEPS)</div>
          <div className="text-2xl font-heading font-semibold text-agro-green">{fmtL(totalPEPS)}</div>
        </div>
        <div className="bg-card border border-border/60 rounded-lg p-4" data-testid="kpi-total-pond">
          <div className="text-[10px] uppercase tracking-wider text-text-muted mb-1.5">Valor inventario (Ponderado)</div>
          <div className="text-2xl font-heading font-semibold text-agro-yellow">{fmtL(totalPond)}</div>
        </div>
        <div className="bg-card border border-border/60 rounded-lg p-4">
          <div className="text-[10px] uppercase tracking-wider text-text-muted mb-1.5">Diferencia</div>
          <div className={`text-2xl font-heading font-semibold ${totalPond - totalPEPS >= 0 ? "text-text-primary" : "text-red-400"}`}>{fmtL(totalPond - totalPEPS)}</div>
        </div>
      </div>

      <div className="bg-card border border-agro-yellow/20 rounded-lg p-4 mb-4 text-xs text-text-secondary leading-relaxed">
        <strong className="text-agro-yellow flex items-center gap-2 mb-1.5"><ChartLineIcon size={14} /> ¿Cómo se calcula?</strong>
        <div className="space-y-1">
          <div>• <strong className="text-agro-green">PC PEPS (real):</strong> costo del ingreso que sale (en semillas, el de fecha de test más antigua). Se usa para rentabilidad y costo de venta reales.</div>
          <div>• <strong className="text-text-primary">PC Ponderado (visual):</strong> Σ(costo × existencia) ÷ Σ existencias. Solo para visualización del costo frente a la existencia actual.</div>
        </div>
      </div>

      <div className="bg-card border border-border/60 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-sidebar text-[10px] uppercase tracking-wider text-text-muted">
              <tr className="text-left">
                <th className="p-3 pl-5">Código</th>
                <th className="p-3">Categoría / SKU</th>
                <th className="p-3 text-right">Stock</th>
                <th className="p-3 text-right">PC PEPS</th>
                <th className="p-3 text-right">PC Ponderado</th>
                <th className="p-3 text-right">Δ unitaria</th>
                <th className="p-3 text-right pr-5">Δ total</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="7" className="p-8 text-center text-text-muted">Cargando…</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan="7" className="p-12 text-center text-text-muted">
                  <BoxArrowDownIcon size={32} className="inline mb-2 opacity-40" />
                  <div>No hay productos para reportar</div>
                </td></tr>
              ) : rows.map((r) => (
                <tr key={r.producto_id} className="border-t border-border/40 hover:bg-white/[0.02]" data-testid={`double-cost-row-${r.codigo}`}>
                  <td className="p-3 pl-5 font-mono text-xs">
                    <div className="flex items-center gap-1.5">
                      <span className="text-agro-green">{r.codigo}</span>
                      {r.es_semilla && <FlowerIcon size={12} weight="fill" className="text-agro-yellow" />}
                    </div>
                  </td>
                  <td className="p-3">
                    <div className="text-sm">{r.categoria}</div>
                    <div className="text-[11px] text-text-muted">{r.sku}</div>
                  </td>
                  <td className="p-3 text-right">{fmtNum(r.stock_actual)}</td>
                  <td className="p-3 text-right font-medium text-agro-green">{fmtL(r.pc_peps)}</td>
                  <td className="p-3 text-right text-text-secondary">{fmtL(r.pc_ponderado)}</td>
                  <td className={`p-3 text-right ${Math.abs(r.diferencia) > 0.01 ? (r.diferencia > 0 ? "text-agro-yellow" : "text-red-400") : "text-text-muted"}`}>
                    {r.diferencia > 0 ? "+" : ""}{fmtL(r.diferencia)}
                  </td>
                  <td className={`p-3 pr-5 text-right ${Math.abs(r.diferencia * r.stock_actual) > 0.01 ? (r.diferencia * r.stock_actual > 0 ? "text-agro-yellow" : "text-red-400") : "text-text-muted"}`}>
                    {r.diferencia * r.stock_actual > 0 ? "+" : ""}{fmtL(r.diferencia * r.stock_actual)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {closeOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" {...m.overlayProps}>
          <div className="bg-card border border-border rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="close-period-modal">
            <div className="p-5 border-b border-border flex items-center justify-between sticky top-0 bg-card z-10">
              <div>
                <h3 className="font-heading text-lg font-semibold">Cierre de período — Fijar P0</h3>
                <div className="text-xs text-text-muted mt-1">El P0 (especial) se fija al cerrar el mes. Sugerencia = max(P1, PC PEPS × 1.5).</div>
              </div>
              <button aria-label="Cerrar" onClick={m.cerrar} className="text-text-muted hover:text-text-primary"><XIcon size={20} /></button>
            </div>
            <div className="p-5">
              <div className="mb-4 flex items-center gap-3">
                <label className="text-sm text-text-secondary">Mes:</label>
                <input
                  type="month"
                  data-testid="close-period-mes"
                  value={mes}
                  onChange={(e) => setMes(e.target.value)}
                  className="bg-app border border-border rounded-md px-3 py-1.5 text-sm focus:border-agro-green"
                />
                <button onClick={loadPreview} className="px-3 py-1.5 text-xs border border-border rounded-md hover:border-agro-green/40">Recalcular sugerencia</button>
              </div>
              <div className="overflow-x-auto border border-border/60 rounded-md">
                <table className="w-full text-sm">
                  <thead className="bg-sidebar text-[10px] uppercase tracking-wider text-text-muted">
                    <tr>
                      <th className="p-2 pl-4 text-left">Incluir</th>
                      <th className="p-2 text-left">Código</th>
                      <th className="p-2 text-right">PC PEPS</th>
                      <th className="p-2 text-right">P1 actual</th>
                      <th className="p-2 text-right">P0 actual</th>
                      <th className="p-2 text-right">P0 sugerido</th>
                      <th className="p-2 pr-4 text-right">P0 a fijar (L.)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.map((p) => (
                      <tr key={p.producto_id} className="border-t border-border/40">
                        <td className="p-2 pl-4">
                          <input
                            type="checkbox"
                            data-testid={`close-include-${p.codigo}`}
                            checked={selected[p.producto_id]?.include ?? true}
                            onChange={(e) => setSelected({ ...selected, [p.producto_id]: { ...(selected[p.producto_id] || { p0: p.p0_sugerido }), include: e.target.checked } })}
                            className="accent-agro-green"
                          />
                        </td>
                        <td className="p-2 font-mono text-xs">{p.codigo} <span className="text-text-muted">{p.sku}</span></td>
                        <td className="p-2 text-right text-agro-green">{fmtL(p.pc_peps)}</td>
                        <td className="p-2 text-right text-text-secondary">{fmtL(p.p1)}</td>
                        <td className="p-2 text-right text-text-secondary">{fmtL(p.p0_actual)} {p.p0_fijado_en && <span className="text-[10px] text-text-muted ml-1">({p.p0_fijado_en})</span>}</td>
                        <td className="p-2 text-right text-agro-yellow">{fmtL(p.p0_sugerido)}</td>
                        <td className="p-2 pr-4 text-right">
                          <input
                            type="number" step="any"
                            data-testid={`close-p0-${p.codigo}`}
                            value={selected[p.producto_id]?.p0 ?? p.p0_sugerido}
                            onChange={(e) => setSelected({ ...selected, [p.producto_id]: { ...(selected[p.producto_id] || { include: true }), p0: e.target.value } })}
                            className="w-24 bg-app border border-border rounded px-2 py-1 text-xs text-right focus:border-agro-green"
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="flex justify-end gap-3 pt-5 border-t border-border/60 mt-5">
                <button onClick={m.cerrar} className="px-4 py-2 text-sm border border-border rounded-md text-text-secondary">Cancelar</button>
                <button disabled={m.saving} onClick={m.guardar} data-testid="close-period-confirm" className="px-5 py-2 text-sm bg-agro-yellow text-black font-medium rounded-md hover:opacity-90">
                  Fijar P0 para {mes}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
